/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { remoteSessionIssuersCreate } from "../funcs/remoteSessionIssuersCreate.js";
import { remoteSessionIssuersDelete } from "../funcs/remoteSessionIssuersDelete.js";
import { remoteSessionIssuersFetchMetadata } from "../funcs/remoteSessionIssuersFetchMetadata.js";
import { remoteSessionIssuersGet } from "../funcs/remoteSessionIssuersGet.js";
import { remoteSessionIssuersGetDuplicatePreflight } from "../funcs/remoteSessionIssuersGetDuplicatePreflight.js";
import { remoteSessionIssuersList } from "../funcs/remoteSessionIssuersList.js";
import { remoteSessionIssuersRefreshMetadata } from "../funcs/remoteSessionIssuersRefreshMetadata.js";
import { remoteSessionIssuersUpdate } from "../funcs/remoteSessionIssuersUpdate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { RemoteSessionIssuer } from "../models/components/remotesessionissuer.js";
import { RemoteSessionIssuerDraft } from "../models/components/remotesessionissuerdraft.js";
import { RemoteSessionIssuerDuplicatePreflight } from "../models/components/remotesessionissuerduplicatepreflight.js";
import { RemoteSessionIssuerRefresh } from "../models/components/remotesessionissuerrefresh.js";
import {
  CreateRemoteSessionIssuerRequest,
  CreateRemoteSessionIssuerSecurity,
} from "../models/operations/createremotesessionissuer.js";
import {
  DeleteRemoteSessionIssuerRequest,
  DeleteRemoteSessionIssuerSecurity,
} from "../models/operations/deleteremotesessionissuer.js";
import {
  FetchRemoteSessionIssuerMetadataRequest,
  FetchRemoteSessionIssuerMetadataSecurity,
} from "../models/operations/fetchremotesessionissuermetadata.js";
import {
  GetRemoteSessionIssuerRequest,
  GetRemoteSessionIssuerSecurity,
} from "../models/operations/getremotesessionissuer.js";
import {
  GetRemoteSessionIssuerDuplicatePreflightRequest,
  GetRemoteSessionIssuerDuplicatePreflightSecurity,
} from "../models/operations/getremotesessionissuerduplicatepreflight.js";
import {
  ListRemoteSessionIssuersRequest,
  ListRemoteSessionIssuersResponse,
  ListRemoteSessionIssuersSecurity,
} from "../models/operations/listremotesessionissuers.js";
import {
  RefreshRemoteSessionIssuerMetadataRequest,
  RefreshRemoteSessionIssuerMetadataSecurity,
} from "../models/operations/refreshremotesessionissuermetadata.js";
import {
  UpdateRemoteSessionIssuerRequest,
  UpdateRemoteSessionIssuerSecurity,
} from "../models/operations/updateremotesessionissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class RemoteSessionIssuers extends ClientSDK {
  /**
   * createRemoteSessionIssuer remoteSessionIssuers
   *
   * @remarks
   * Create a new remote_session_issuer.
   */
  async create(
    request: CreateRemoteSessionIssuerRequest,
    security?: CreateRemoteSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionIssuer> {
    return unwrapAsync(remoteSessionIssuersCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteRemoteSessionIssuer remoteSessionIssuers
   *
   * @remarks
   * Soft-delete a remote_session_issuer. Blocked if any remote_session_clients still reference it.
   */
  async delete(
    request: DeleteRemoteSessionIssuerRequest,
    security?: DeleteRemoteSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(remoteSessionIssuersDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * fetchRemoteSessionIssuerMetadata remoteSessionIssuers
   *
   * @remarks
   * Hit an upstream issuer's RFC 8414 .well-known/oauth-authorization-server document and return a draft suitable for createRemoteSessionIssuer. Keyed by issuer URL; no record need exist and nothing is persisted. Use refreshMetadata to re-discover and persist against an existing issuer.
   */
  async fetchMetadata(
    request: FetchRemoteSessionIssuerMetadataRequest,
    security?: FetchRemoteSessionIssuerMetadataSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionIssuerDraft> {
    return unwrapAsync(remoteSessionIssuersFetchMetadata(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getRemoteSessionIssuer remoteSessionIssuers
   *
   * @remarks
   * Get a remote_session_issuer by id, by slug, or by upstream issuer URL. Provide exactly one.
   *
   * Looking up by issuer is how an automatic setup flow decides whether an upstream authorization server already has an identity provider before creating one: a 404 means nothing describes that URL yet, so create it. Unlike id and slug, which address at most one record, several issuers may legitimately describe the same URL — a project may keep its own alongside one inherited from its organization or from the platform catalog. This returns the one this project would use, preferring project over organization over platform and, within a tier, the oldest.
   *
   * The issuer URL is canonicalized before matching: scheme and host are lowercased, the scheme's default port is dropped, and trailing slashes are stripped. http and https are deliberately NOT equated, path case is significant, and a URL carrying a query or fragment is rejected (RFC 8414 forbids both on issuer identifiers). Canonicalization applies to the supplied URL only, never to stored values, so an issuer recorded with an unusual spelling may not be found and a duplicate is created instead, which is the safe direction to fail.
   */
  async get(
    request?: GetRemoteSessionIssuerRequest | undefined,
    security?: GetRemoteSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionIssuer> {
    return unwrapAsync(remoteSessionIssuersGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getRemoteSessionIssuerDuplicatePreflight remoteSessionIssuers
   *
   * @remarks
   * Report the existing remote_session_issuers that already describe an upstream issuer URL, so a create or edit form can warn before it duplicates one. Covers this project's own issuers plus those inherited from the organization and the platform catalog.
   *
   * Advisory only. Duplicating an issuer URL is legitimate — a project may want its own record so it can attach different documentation, branding or scopes — so nothing here blocks a write, and no lock is taken. A create that races another create still produces two records, which is a supported state.
   *
   * Matching uses the same canonicalization as getRemoteSessionIssuer. A URL that cannot be parsed as an issuer identifier returns no matches rather than an error, because a partially typed URL is the normal state of a form field.
   */
  async getDuplicatePreflight(
    request?: GetRemoteSessionIssuerDuplicatePreflightRequest | undefined,
    security?: GetRemoteSessionIssuerDuplicatePreflightSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionIssuerDuplicatePreflight> {
    return unwrapAsync(remoteSessionIssuersGetDuplicatePreflight(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listRemoteSessionIssuers remoteSessionIssuers
   *
   * @remarks
   * List the remote_session_issuers the caller's project can use: its own, plus those inherited from its organization and from the platform catalog. Newest first; the search, upstream_host and tier filters narrow the listing without changing its order or cursor.
   */
  async list(
    request?: ListRemoteSessionIssuersRequest | undefined,
    security?: ListRemoteSessionIssuersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<
    PageIterator<ListRemoteSessionIssuersResponse, { cursor: string }>
  > {
    return unwrapResultIterator(remoteSessionIssuersList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * refreshRemoteSessionIssuerMetadata remoteSessionIssuers
   *
   * @remarks
   * Re-fetch an existing remote_session_issuer's RFC 8414 metadata document and persist the discovered values. Keyed by issuer id. Only RFC 8414-derived columns are written — endpoints, the *_supported arrays, client_id_metadata_document_supported, and the documentation URLs. Speakeasy behavior and display fields (oidc, passthrough, name, slug, logo, client setup documentation) are left alone. Requires project:write.
   */
  async refreshMetadata(
    request: RefreshRemoteSessionIssuerMetadataRequest,
    security?: RefreshRemoteSessionIssuerMetadataSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionIssuerRefresh> {
    return unwrapAsync(remoteSessionIssuersRefreshMetadata(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateRemoteSessionIssuer remoteSessionIssuers
   *
   * @remarks
   * Update fields on an existing remote_session_issuer.
   */
  async update(
    request: UpdateRemoteSessionIssuerRequest,
    security?: UpdateRemoteSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionIssuer> {
    return unwrapAsync(remoteSessionIssuersUpdate(
      this,
      request,
      security,
      options,
    ));
  }
}
